import apiClient from './apiClient';

export interface NotificationDto {
  notificationId: string;
  notificationType: string;
  title: string;
  message: string;
  linkUrl?: string;
  isRead: boolean;
  createdAt: string;
  readAt?: string;
}

export interface NotificationPreferenceDto {
  notificationType: string;
  displayName: string;
  module: string;
  description: string;
  enabled: boolean;
}

export interface NotificationPreferencesDto {
  userId: string;
  userName: string;
  emailNotificationsEnabled: boolean;
  preferences: NotificationPreferenceDto[];
}

export interface NotificationPreferenceUserDto {
  userId: string;
  fullName: string;
  email: string;
}

export interface NotificationTemplateBookmarkDto {
  token: string;
  description: string;
  example?: string;
}

export interface NotificationTemplateDto {
  notificationType: string;
  displayName: string;
  module: string;
  description: string;
  emailEnabled: boolean;
  bellEnabled: boolean;
  emailSubject: string;
  emailBody: string;
  bellTitle: string;
  bellMessage: string;
  availableBookmarks: NotificationTemplateBookmarkDto[];
  isCustomized: boolean;
}

export type UpdateNotificationTemplateRequest = Pick<NotificationTemplateDto,
  'emailEnabled' | 'bellEnabled' | 'emailSubject' | 'emailBody' | 'bellTitle' | 'bellMessage'>;

const notificationApi = {
  getNotifications: (unreadOnly?: boolean) =>
    apiClient.get<{ data: { items: NotificationDto[]; unreadCount: number } }>('/api/notifications', {
      params: unreadOnly ? { unreadOnly: true } : undefined,
    }),
  getPreferenceUsers: () =>
    apiClient.get<{ data: NotificationPreferenceUserDto[] }>('/api/notifications/preferences/users'),
  getPreferences: (userId?: string) =>
    apiClient.get<{ data: NotificationPreferencesDto }>('/api/notifications/preferences', {
      params: userId ? { userId } : undefined,
    }),
  updatePreferences: (data: Partial<NotificationPreferencesDto> & { userId?: string }) =>
    apiClient.put('/api/notifications/preferences', data),
  getTemplates: (organisationId: string) =>
    apiClient.get<{ data: NotificationTemplateDto[] }>('/api/notifications/templates', { params: { organisationId } }),
  updateTemplate: (organisationId: string, notificationType: string, data: UpdateNotificationTemplateRequest) =>
    apiClient.put<{ data: NotificationTemplateDto }>(`/api/notifications/templates/${encodeURIComponent(notificationType)}`, { ...data, organisationId }),
  resetTemplate: (organisationId: string, notificationType: string) =>
    apiClient.post<{ data: NotificationTemplateDto }>(`/api/notifications/templates/${encodeURIComponent(notificationType)}/reset`, { organisationId }),
  markAsRead: (id: string) => apiClient.put(`/api/notifications/${id}/mark-read`),
  markAllAsRead: () => apiClient.put('/api/notifications/mark-all-read'),
  deleteNotification: (id: string) => apiClient.delete(`/api/notifications/${id}`),
};

export default notificationApi;
